import { FC, useEffect, useRef, useState } from "react";
import DbIcon from "designs/DesignB/components/icons";

const reducedMotion = () =>
  typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * The panel's photos (1NOW-11219): one scroll-snap track, so a swipe is the browser's own, with
 * arrows, the arrow keys and an "n / N" counter. Hosts upload originals of up to 4.6 MB with no
 * smaller copy, so only the photo on screen and its two neighbours are given a src; one that
 * has been loaded stays loaded.
 */
const SwipeGallery: FC<{ photos: string[]; name: string; className?: string }> = ({ photos, name, className = "" }) => {
  const track = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState(0);
  const [seen, setSeen] = useState<Set<number>>(() => new Set([0, 1]));
  const [failed, setFailed] = useState<Set<number>>(() => new Set());
  const n = photos.length;

  useEffect(() => {
    setSeen((prev) => (prev.has(at - 1) && prev.has(at) && prev.has(at + 1) ? prev : new Set([...prev, at - 1, at, at + 1])));
  }, [at]);

  if (n === 0) {
    return (
      <div className={`db-gal ${className}`}>
        <div className="db-fc__placeholder">
          <DbIcon name="car" className="h-12 w-12" />
          <span>Photo coming soon</span>
        </div>
      </div>
    );
  }

  const go = (to: number) => {
    const el = track.current;
    if (!el) return;
    const i = Math.max(0, Math.min(n - 1, to));
    el.scrollTo({ left: i * el.clientWidth, behavior: reducedMotion() ? "auto" : "smooth" });
    setAt(i);
  };

  return (
    <div className={`db-gal ${className}`} role="group" aria-roledescription="carousel" aria-label={`Photos of ${name}`}>
      <div
        ref={track}
        className="db-gal__track"
        tabIndex={0}
        aria-label={`Photo ${at + 1} of ${n}`}
        onScroll={(e) => {
          const el = e.currentTarget;
          if (el.clientWidth) setAt(Math.round(el.scrollLeft / el.clientWidth));
        }}
        onKeyDown={(e) => {
          if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
          e.preventDefault();
          e.stopPropagation();
          go(at + (e.key === "ArrowLeft" ? -1 : 1));
        }}
      >
        {photos.map((src, i) => (
          <div key={src} className="db-gal__slide" aria-roledescription="slide" aria-label={`${i + 1} of ${n}`}>
            {seen.has(i) && !failed.has(i) && (
              <img
                src={src}
                alt=""
                decoding="async"
                draggable={false}
                onError={() => setFailed((prev) => new Set(prev).add(i))}
              />
            )}
          </div>
        ))}
      </div>
      {n > 1 && (
        <>
          <button
            type="button"
            className="db-gal__nav db-gal__nav--prev"
            aria-label="Previous photo"
            disabled={at === 0}
            onClick={(e) => {
              e.stopPropagation();
              go(at - 1);
            }}
          >
            <DbIcon name="chevronLeft" className="h-[18px] w-[18px]" />
          </button>
          <button
            type="button"
            className="db-gal__nav db-gal__nav--next"
            aria-label="Next photo"
            disabled={at >= n - 1}
            onClick={(e) => {
              e.stopPropagation();
              go(at + 1);
            }}
          >
            <DbIcon name="chevronRight" className="h-[18px] w-[18px]" />
          </button>
          <span className="db-gal__count" aria-hidden="true">
            {at + 1} / {n}
          </span>
        </>
      )}
    </div>
  );
};

export default SwipeGallery;
